/**
 * Bandeau affiché en haut de l'inspecteur quand un calque REG est sélectionné.
 *
 * Rôle :
 *   - Confirmer visuellement à l'utilisateur que le calque est protégé.
 *   - Afficher l'UFI source + horodatage du dernier fetch.
 *   - Proposer un bouton "Rafraîchir" qui re-récupère la fiche et met à
 *     jour tous les calques du même blockId (positions préservées).
 *
 * Le bandeau est compact : il ne remplace pas l'inspecteur classique
 * (police, taille, couleur restent éditables), il l'augmente.
 */
import React from 'react';
import { useLabelStore } from '../store/useLabelStore';
import { fetchRegulatoryByUfi } from '../api/regulatoryClient';
import { buildRegulatoryLayers, resolveLabelForCountry } from '../utils/regulatoryBlockBuilder';
import { regBlockMatchKey, lotAvecZones, gardeFauteDeZones, estFicheMasqueeDEssai } from '../utils/regulatoryKey';
import { partielDeContenu } from '../utils/regulatoryTemplate';
import { REGULATORY_CONTENT_EDIT_ALLOWED } from '../utils/regulatoryFlags';
import { fitElementsToContent } from '../utils/fitFrame';


function formatRelativeTime(iso) {
  if (!iso) return '';
  const t = new Date(iso).getTime();
  if (Number.isNaN(t)) return '';
  const delta = Math.max(0, Date.now() - t);
  const minutes = Math.floor(delta / 60000);
  if (minutes < 1) return 'à l\'instant';
  if (minutes < 60) return `il y a ${minutes} min`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `il y a ${hours} h`;
  const days = Math.floor(hours / 24);
  return `il y a ${days} j`;
}


export default function RegulatoryInspectorBanner({ el }) {
  const [refreshing, setRefreshing] = React.useState(false);
  const [error, setError] = React.useState(null);
  const [info, setInfo] = React.useState(null);
  const { elements, canvas, updateElement, removeElements, addElement, selectElement } = useLabelStore();

  if (!el || !el.regBlock) return null;

  const { blockId, ufi, role, fetched_at } = el.regBlock;
  const relative = formatRelativeTime(fetched_at);

  const handleRefresh = async () => {
    setError(null);
    setInfo(null);
    setRefreshing(true);
    try {
      const payload = await fetchRegulatoryByUfi(ufi);
      // Rejoue la même variante pays que celle choisie à l'import (nouveau schéma
      // `labels`). Fallback sur la variante par défaut pour les anciens blocs.
      const country = el.regBlock.country || null;
      // Avant, si la fiche ne proposait plus la variante du bloc (CH, BE…),
      // `resolveLabelForCountry` retombait sur la variante par défaut et le
      // rafraîchissement remplaçait les textes du pays par ceux du pays
      // principal, sans un mot. On préfère ne rien écrire et le dire.
      // La fiche peut arriver sous deux formes — le dictionnaire `labels` par
      // pays, ou un unique `label` (resolveLabelForCountry accepte les deux) :
      // on ne conclut donc jamais sur l'absence de `labels`, on distingue les
      // deux échecs, car un texte réglementaire écrasé part à l'impression.
      if (country) {
        const variantes = (payload.labels && typeof payload.labels === 'object') ? payload.labels : null;
        if (variantes && !variantes[country]) {
          throw new Error(`La fiche ne propose plus de variante ${country} : rafraîchissement annulé pour ne pas écraser les textes ${country}.`);
        }
        if (!variantes) {
          throw new Error(`Cette fiche ne renvoie qu'une étiquette, sans distinction de pays : impossible de confirmer qu'il s'agit de la variante ${country}. Rafraîchissement annulé pour ne pas écraser les textes ${country} — pour les mettre à jour, réimporte le bloc par le bouton ${country} de l'arbre « Par client ».`);
        }
      }
      payload.label = resolveLabelForCountry(payload, country);
      // La réponse per-UFI ne porte ni `_country` ni `_catalog` : sans ces deux
      // marqueurs, `buildRegBlockMeta` réécrivait `country: null`, `catalog: null`,
      // `source: 'ufi'` et un blockId `reg-<ufi>` à la place de `reg-cat-…`. Le
      // bloc perdait donc sa variante pays — au rafraîchissement SUIVANT, une
      // étiquette CH ou BE revenait silencieusement aux textes par défaut — ainsi
      // que sa trace client / gamme / produit. On rejoue l'identité du bloc.
      payload._country = country;
      if (el.regBlock.catalog) payload._catalog = { ...el.regBlock.catalog };
      // Construit la nouvelle liste de calques pour ce bloc.
      const newLayers = buildRegulatoryLayers(payload, canvas);

      // Index les calques existants du bloc par role pour préserver leurs
      // positions / tailles. Les pictogrammes ont une sous-clé pictogramCode
      // pour matcher précisément GHS02 → GHS02.
      // (Le distributeur masqué d'une étiquette d'essai reste hors du rafraîchissement : regulatoryKey.js.)
      const existing = elements.filter(
        e => e.regBlock && e.regBlock.blockId === blockId && !estFicheMasqueeDEssai(e)
      );
      // Index en LISTES, pas en valeur unique. Un calque réglementaire dupliqué
      // (Ctrl+D pour répéter l'UFI sur un rabat) porte le MÊME rôle : l'index
      // ne retenait que le dernier, si bien que le rafraîchissement mettait à
      // jour la copie et SUPPRIMAIT l'original — celui qui était placé au propre.
      const existingByKey = {};
      for (const e of existing) {
        const k = regBlockMatchKey(e.regBlock);
        (existingByKey[k] = existingByKey[k] || []).push(e);
      }

      const toKeep = new Set();
      const remplaces = new Set();
      // Rôles que la NOUVELLE fiche produit : un calque portant l'un d'eux ne
      // doit jamais être supprimé, même si l'appariement ne l'a pas retenu.
      const clesProduites = new Set(newLayers.map((l) => regBlockMatchKey(l.regBlock)));
      // Calques touchés par ce rafraîchissement : leur texte a changé, donc leur
      // cadre doit se réajuster au contenu (cf. utils/fitFrame).
      const idsTouches = [];
      let updated = 0;
      let added = 0;
      for (const layer of newLayers) {
        const key = regBlockMatchKey(layer.regBlock);
        const correspondants = existingByKey[key] || [];
        if (correspondants.length === 0) {
          // Nouveau calque (ex: nouveau picto ajouté côté API).
          const nouvelId = crypto.randomUUID();
          addElement({ ...layer, id: nouvelId });
          idsTouches.push(nouvelId);
          added++;
          continue;
        }
        // Chaque exemplaire est mis à jour, en gardant SA place.
        for (const match of correspondants) {
          // Si le type du calque change (ex: ancien picto en `image` →
          // nouveau format en `shape` SVG local), on ne peut pas faire un
          // update partiel : il faut remplacer le calque. On conserve
          // position/taille/rotation ET l'identité (groupe, verrou, masquage)
          // de l'ancien pour ne pas casser la mise en page de l'utilisateur.
          if (match.type !== layer.type) {
            const remplacantId = crypto.randomUUID();
            removeElements([match.id]);
            remplaces.add(match.id);
            addElement({
              ...layer,
              id: remplacantId,
              x: match.x, y: match.y, width: match.width, height: match.height,
              rotation: match.rotation || 0,
              parentId: match.parentId ?? null,
              locked: !!match.locked,
              hidden: !!match.hidden,
            });
            idsTouches.push(remplacantId);
          } else {
            // Mise à jour du CONTENU seul, en préservant placement et mise en
            // forme. `partielDeContenu` remet aussi `content` à zéro sur un
            // texte : sans cela, un calque corrigé une fois au double-clic
            // gardait à l'écran ET au PDF l'ANCIENNE valeur réglementaire,
            // pendant que ce bandeau annonçait « mis à jour ».
            updateElement(match.id, { ...partielDeContenu(layer, match), name: layer.name });
            toKeep.add(match.id);
            idsTouches.push(match.id);
          }
          updated++;
        }
      }

      // Supprime les calques du bloc qui n'existent plus dans le nouveau JSON.
      // Deux garde-fous : ne pas repasser un calque DÉJÀ retiré par un
      // remplacement (entrée d'historique fantôme et compteur faux), et ne
      // jamais retirer un calque dont le rôle EST produit par la fiche.
      // Troisième garde-fou : une fiche relue SANS zones `print` ne retire pas les
      // mentions que seules les zones posent (avertissement sanitaire, composition,
      // phrases P, notice…) — elle ne les sert pas, elle ne prouve pas qu'elles ont
      // disparu (relecture de #336, 19/10). Gardées telles quelles, et dit.
      const avecZones = lotAvecZones(newLayers);
      const restes = existing
        .filter(e => !toKeep.has(e.id)
                  && !remplaces.has(e.id)
                  && !clesProduites.has(regBlockMatchKey(e.regBlock)));
      const gardes = restes.filter(e => gardeFauteDeZones(e, avecZones));
      const removed = restes.filter(e => !gardeFauteDeZones(e, avecZones)).map(e => e.id);
      if (removed.length > 0) removeElements(removed);

      // `removeElements` vide la sélection, et l'inspecteur ne rend ce bandeau
      // que sur une sélection d'UN calque : sans cette resélection, le bandeau
      // se démontait avant l'affichage du compte rendu et l'utilisateur ne
      // voyait RIEN de ce qui venait de se passer — retraits compris.
      if (removed.length > 0 && idsTouches.length > 0) selectElement(idsTouches[0]);

      // Le texte a changé : le cadre doit suivre. Non attendu — le bandeau rend
      // la main tout de suite, l'ajustement se pose deux images plus tard.
      fitElementsToContent(idsTouches, { nomHistorique: 'Rafraîchir la fiche · ajusté au contenu' })
        .catch(() => {});

      const parts = [];
      if (updated) parts.push(`${updated} mis à jour`);
      if (added) parts.push(`${added} ajouté${added > 1 ? 's' : ''}`);
      if (removed.length) parts.push(`${removed.length} retiré${removed.length > 1 ? 's' : ''}`);
      const garde = gardes.length
        ? ` ${gardes.length} mention${gardes.length > 1 ? 's' : ''} gardée${gardes.length > 1 ? 's' : ''} telle${gardes.length > 1 ? 's' : ''} quelle${gardes.length > 1 ? 's' : ''} (${gardes.map((e) => e.name).join(', ')}) : la fiche relue ne porte pas les zones prêtes à imprimer, elle ne les met pas à jour.`
        : '';
      setInfo(parts.length ? `Fiche rafraîchie · ${parts.join(', ')}.${garde}` : `Fiche déjà à jour.${garde}`);
    } catch (e) {
      setError(e.message || 'Impossible de rafraîchir la fiche.');
    } finally {
      setRefreshing(false);
      setTimeout(() => { setInfo(null); setError(null); }, 6000);
    }
  };

  return (
    <div className="mb-4 rounded-xl border border-orange-500/40 bg-orange-500/10 p-3">
      <div className="flex items-center gap-2 mb-2">
        <span className="w-2 h-2 rounded-full bg-orange-500 shadow-[0_0_6px_rgba(249,115,22,0.7)]" />
        <span className="text-[10px] font-mono font-bold uppercase tracking-widest text-orange-300">
          Calque réglementaire
        </span>
      </div>
      <p className="text-[10px] font-sans text-orange-100/70 leading-snug mb-2">
        {REGULATORY_CONTENT_EDIT_ALLOWED ? (
          <>Édition du texte <strong>temporairement autorisée</strong> pour corriger les données importées.{' '}
          <span className="text-orange-300">⚠ « Rafraîchir » ré-importe la fiche et écrase vos corrections.</span></>
        ) : (
          <>Contenu protégé · le texte ne peut pas être modifié. La mise en forme
          (police, taille, couleur, position) reste libre.</>
        )}
      </p>
      <div className="text-[9px] font-mono text-white/50 space-y-0.5 mb-3">
        {/* Repli sur un tiret comme la ligne « Mis à jour » : sur un bloc importé
            par client sans UFI, `regBlock.ufi` est null et cette ligne affichait
            un vide, qu'on lisait comme un défaut d'affichage. */}
        <div><span className="text-white/40">UFI source</span> : <span className="text-white/80">{ufi || '—'}</span></div>
        {role && <div><span className="text-white/40">Type</span> : <span className="text-white/80">{role}</span></div>}
        <div><span className="text-white/40">Mis à jour</span> : <span className="text-white/80">{relative || '—'}</span></div>
      </div>
      <button
        onClick={handleRefresh}
        disabled={refreshing}
        title="Rafraîchir depuis l'API"
        className="w-full py-2 flex items-center justify-center gap-2 bg-orange-500/15 hover:bg-orange-500/25 border border-orange-500/40 hover:border-orange-500/60 rounded-lg text-[10px] font-mono uppercase tracking-widest text-orange-200 transition-all disabled:opacity-50 disabled:cursor-wait"
      >
        {/* Icône SVG en trait, comme le reste de l'application. Avant, ce bouton
            portait des glyphes collés dans le libellé (un emoji au repos, une
            flèche pendant le chargement) : rendu au hasard de la police du
            système, et lus à voix haute par un lecteur d'écran. */}
        <svg
          width="12" height="12" viewBox="0 0 24 24" fill="none"
          stroke="currentColor" strokeWidth="2"
          className={`shrink-0 ${refreshing ? 'animate-spin' : ''}`}
          aria-hidden="true"
        >
          <polyline points="23 4 23 10 17 10" /><path d="M20.49 15a9 9 0 11-2.12-9.36L23 10" />
        </svg>
        {refreshing ? 'Rafraîchissement…' : 'Rafraîchir depuis l\'API'}
      </button>
      {error && (
        <div className="mt-2 p-2 rounded-md bg-red-500/15 border border-red-500/40 text-[9px] font-mono text-red-300">
          ⚠ {error}
        </div>
      )}
      {info && !error && (
        <div className="mt-2 p-2 rounded-md bg-green-500/15 border border-green-500/40 text-[9px] font-mono text-green-300">
          ✓ {info}
        </div>
      )}
    </div>
  );
}
